import { Injectable, Inject, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
// Models
import { RuleEngineStateServiceModel } from '../models/rule-engine-state.model';
import { 
	AvailableRulesetModel,
	OrderRulesModel,
	RuleCommentModel,
	RuleLogModel,
	RuleModel,
	StipModel
} from '../models';

/**
 * Constant with the injection token, needed for the dynamic rule-engine.state service injection
 */
export const RULE_ENGINE_STATE = new InjectionToken<any>('rule-engine.state');

/**
 * RuleEngineState load factory
 * 
 * @param data 
 * @returns 
 */
export function loadRulesStateConfig(ruleEngineState: RuleEngineStateServiceModel): RuleEngineStateService {
	const service = new RuleEngineStateService(ruleEngineState);

	return service;
}

@Injectable()
export class RuleEngineStateService implements RuleEngineStateServiceModel {
	public ruleEngineStateWrapper: RuleEngineStateServiceModel;

	constructor(@Inject(RULE_ENGINE_STATE) ruleEngineState: RuleEngineStateServiceModel) {
		this.ruleEngineStateWrapper = ruleEngineState;
	}

	/**
	 * Call the module state for OrderRules
	 */
	getOrderRules(): OrderRulesModel {
		return this.ruleEngineStateWrapper.getOrderRules();
	}

	/**
	 * Call the module state for RulesCompareInfo
	 */
	getRuleCompareInfo(): string[] {
		return this.ruleEngineStateWrapper.getRuleCompareInfo();
	}

	/**
	 *  Call the module state for onRevised (section to prevent the add manual rules)
	 */
	getSelectedOnRevised(): boolean {
		return this.ruleEngineStateWrapper.getSelectedOnRevised();
	}

	/**
	 *  Call the module state for OrderRuleSets (applied ruleSets)
	 */
	getOrderRuleSets(): string[] {
		return this.ruleEngineStateWrapper.getOrderRuleSets();
	}

	/**
	 *  Call the module state for AvailableRuleSets
	 */
	getAvailableRuleSets(): AvailableRulesetModel[] {
		return this.ruleEngineStateWrapper.getAvailableRuleSets();
	}

	/**
	 * doc must exists
	 * 
	 * @param violation 
	 */
	AddManualRule(violation: any): void {
		return this.ruleEngineStateWrapper.AddManualRule(violation);
	}

	/**
	 * doc must exist
	 * 
	 * @param ruleName 
	 * @param notes 
	 */
	AddCommentRule(ruleName: string, notes: any): void {
		this.ruleEngineStateWrapper.AddCommentRule(ruleName, notes);
	}

	/**
	 * doc must exist
	 * 
	 * @param ruleName 
	 * @param stips 
	 */
	AddStipRule(ruleName: string, stips: any): void {
		this.ruleEngineStateWrapper.AddStipRule(ruleName, stips);
	}

	/**
	* doc must exist
	* 
	* @param ruleSets 
	*/
	SetAvailableRuleSet(ruleSets: any): void {
		this.ruleEngineStateWrapper.SetAvailableRuleSet(ruleSets);
	}

	/**
	 * doc must exist
	 * 
	 * @param id 
	 */
	SetOrderRuleSets(orderId: number): void {
		this.ruleEngineStateWrapper.SetOrderRuleSets(orderId);
	}

	/**
	 * doc must exist
	 * 
	 * @param orderId 
	 * @param ruleSet 
	 */
	SetDesktopOrderRules(orderId: number, ruleSet: string): void {
		this.ruleEngineStateWrapper.SetDesktopOrderRules(orderId, ruleSet);
	}

	/**
	 * doc must exist
	 * 
	 * @param uiSection 
	 * @param canSave 
	 * @param check 
	 */
	SetRulesCheckedRules(uiSection: string, canSave: boolean, check: number): void {
		this.ruleEngineStateWrapper.SetRulesCheckedRules(uiSection, canSave, check);
	}

	/**
	 * doc must exist
	 * 
	 * @param id 
	 */
	SetRulesCompareInfo(orderId: number): void {
		this.ruleEngineStateWrapper.SetRulesCompareInfo(orderId);
	}

	/**
	 * doc must exist
	 * 
	 * @param onlyUncheckedRules 
	 */
	SetUncheckedRules(onlyUncheckedRules: boolean): void {
		this.ruleEngineStateWrapper.SetUncheckedRules(onlyUncheckedRules);
	}

	/**
	 * doc must exist
	 * 
	 * @param id 
	 */
	SetIsRunningRules(isRunningRules: boolean): void {
		this.ruleEngineStateWrapper.SetIsRunningRules(isRunningRules);
	}

	/**
	 * doc must exist
	 * 
	 * @param ruleName 
	 * @param status 
	 */
	UpdatedRuleStatus(ruleName: string, status: any): void {
		this.ruleEngineStateWrapper.UpdatedRuleStatus(ruleName, status);
	}

	 /**
	  * Doc must exist
	  * 
	  * @param ruleLog 
	  */
	UpdatedRulelog(ruleLog: RuleLogModel[]): void {
		this.ruleEngineStateWrapper.UpdatedRulelog(ruleLog);
	}
 
	 /**
	  * Doc must exist
	  * 
	  * @param violations 
	  */
	UpdatedRuleViolation(violations: RuleModel[]): void {
		this.ruleEngineStateWrapper.UpdatedRuleViolation(violations);
	}
 
	 /**
	  * doc must exist
	  * 
	  * @param updatedRuleStatus 
	  */
	UpdatedMultipleRuleStatus(updatedRuleStatus: RuleModel[]): Promise<any> {
		return this.ruleEngineStateWrapper.UpdatedMultipleRuleStatus(updatedRuleStatus);
	}
 
	 /**
	  * doc must exist
	  * 
	  * @param orderRules 
	  * @param orderDetail
	  */
	UpdatedRuleOrders(orderRules: OrderRulesModel, orderDetail: any): void {
		this.ruleEngineStateWrapper.UpdatedRuleOrders(orderRules, orderDetail);
	}

	/**
	 * doc must exist
	 * 
	 * @param ruleName 
	 * @param deletedNotes 
	 */
	DeleteCommentRule(ruleName: string, deletedNotes: RuleCommentModel[]): void {
		this.ruleEngineStateWrapper.DeleteCommentRule(ruleName, deletedNotes);
	}

	/**
	 * doc must exist
	 * 
	 * @param ruleName 
	 * @param deletedStips 
	 */
	DeleteStipsRule(ruleName: string, deletedStips: StipModel[]): void {
		this.ruleEngineStateWrapper.DeleteStipsRule(ruleName, deletedStips);
	}

	/**
	 * doc must exist
	 */
	ClearRulelog(): void {
		this.ruleEngineStateWrapper.ClearRulelog();
	}

	/**
	 * doc must exist
	 * 
	 * @param channelName 
	 * @param event 
	 * @param param 
	 */
	StoreSignlREvent(channelName: string, event: any, param: string): void {
		this.ruleEngineStateWrapper.StoreSignlREvent(channelName, event, param);
	}

	 /**
	 * doc must exist
	 * 
	 * @returns 
	 */
	getNavItems(): any {
		this.ruleEngineStateWrapper.getNavItems();
	}
}